import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "../components/Brand";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import { login as loginApi } from "../lib/api";
import { getUser, saveUser } from "../lib/auth";
import {
  ArrowRight,
  ShieldCheck,
  ScrollText,
  FileCheck2,
  ExternalLink,
  BadgeCheck,
  ArrowUpRight,
  Users,
  Landmark,
  KeyRound,
  CalendarClock,
} from "lucide-react";

const FAQ = [
  {
    q: "Who is Brickstok?",
    a: "An institutional-grade operational portal facilitating fractional access to prime UK commercial real estate.",
    icon: Landmark,
  },
  {
    q: "What problem does it solve?",
    a: "We eliminate entry barriers to prime commercial assets through fractionalisation, starting from £5,000.",
    icon: KeyRound,
  },
  {
    q: "How does it work?",
    a: "We utilise a ring-fenced SPV model for legal segregation and asset protection.",
    icon: BadgeCheck,
  },
  {
    q: "Security & Oversight",
    a: "We employ robust encryption, KYC/AML protocols, and are structured with independent statutory audit oversight and specialist legal counsel aligned with UK commercial real estate frameworks.",
    icon: ShieldCheck,
  },
  {
    q: "Partnership Status",
    a: "Formal statutory audit and legal counsel appointments are scheduled for our Q3 2026 rollout, aligned with FCA-supervised UK commercial real estate frameworks.",
    icon: CalendarClock,
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(getUser());

  useEffect(() => {
    setUser(getUser());
  }, []);

  const handleEnter = async (e) => {
    e?.preventDefault?.();
    if (user) {
      navigate("/marketplace");
      return;
    }
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email to continue.");
      return;
    }
    setLoading(true);
    try {
      // Seamless demo login — OTP is mocked as 1234 server-side
      const res = await loginApi(email.trim().toLowerCase(), "1234");
      saveUser(res.user);
      toast.success(`Welcome, ${res.user.email}`);
      navigate("/marketplace");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to enter the portal.");
    } finally {
      setLoading(false);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Top anchor nav */}
      <nav
        className="sticky top-0 z-40 w-full border-b border-slate-200/70 bg-white/85 backdrop-blur-md"
        data-testid="landing-nav"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={() => scrollTo("hero")}
            className="text-left"
            data-testid="landing-brand-btn"
          >
            <Brand variant="dark" size="md" />
          </button>
          <div className="hidden items-center gap-8 md:flex">
            <button
              onClick={() => scrollTo("about")}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
              data-testid="nav-about"
            >
              About
            </button>
            <button
              onClick={() => scrollTo("faq")}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
              data-testid="nav-faq"
            >
              FAQ
            </button>
            <button
              onClick={() => scrollTo("signin")}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
              data-testid="nav-signin"
            >
              Sign in
            </button>
          </div>
          {user ? (
            <Button
              onClick={() => navigate("/marketplace")}
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="nav-continue-btn"
            >
              Continue to Portal <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={() => scrollTo("signin")}
              className="text-slate-900 hover:brightness-95"
              style={{ background: "#D4AF37" }}
              data-testid="nav-enter-btn"
            >
              Enter Portal
            </Button>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section id="hero" className="relative overflow-hidden" data-testid="landing-hero">
        <div className="grid min-h-[640px] grid-cols-1 lg:grid-cols-5">
          {/* Left — Brand panel */}
          <div className="relative hidden lg:col-span-3 lg:flex" style={{ background: "#0F172A" }}>
            <div className="absolute inset-0 grain" />
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1603294278610-b5bd0506303e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                mixBlendMode: "luminosity",
              }}
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(120deg, rgba(15,23,42,0.92), rgba(15,23,42,0.65) 60%, rgba(15,23,42,0.85))" }}
            />
            <div className="relative z-10 flex w-full flex-col justify-between p-12">
              <div className="overline" style={{ color: "#D4AF37" }}>UK Commercial Real Estate</div>

              <div className="max-w-xl">
                <h1 className="mt-4 font-display text-5xl font-semibold leading-[1.05] tracking-tight text-white">
                  Institutional access.
                  <br />
                  <span className="italic" style={{ color: "#D4AF37" }}>Fractional</span> ownership.
                </h1>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-300">
                  Access a curated portfolio of prime UK office, retail, and logistics assets from £5,000. Structured, regulated, and settled through AJBN Connect.
                </p>
              </div>

              <div className="grid max-w-xl grid-cols-2 gap-6 border-t border-slate-800 pt-8" data-testid="hero-trust-signals">
                <div>
                  <div className="flex items-center gap-2">
                    <ScrollText className="h-4 w-4" style={{ color: "#D4AF37" }} />
                    <div className="overline" style={{ color: "#94a3b8" }}>Legal Counsel</div>
                  </div>
                  <div className="mt-2 font-display text-lg text-white">Specialist UK CRE</div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <FileCheck2 className="h-4 w-4" style={{ color: "#D4AF37" }} />
                    <div className="overline" style={{ color: "#94a3b8" }}>Statutory Audit</div>
                  </div>
                  <div className="mt-2 font-display text-lg text-white">Independent Oversight</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Sign-in */}
          <div id="signin" className="col-span-1 flex items-center justify-center bg-white p-8 lg:col-span-2" data-testid="landing-signin">
            <div className="w-full max-w-md">
              <div className="lg:hidden mb-8">
                <Brand size="lg" />
              </div>
              <div className="overline">Demo Portal</div>
              <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900" data-testid="login-heading">
                Sign in to Brickstok
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Enter your email to instantly explore the institutional portal. Session persists across pages.
              </p>

              <form onSubmit={handleEnter} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="login-email" className="text-sm font-medium text-slate-700">Email address</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@institution.com"
                    className="mt-2 h-12 text-base"
                    disabled={!!user}
                    data-testid="login-email-input"
                  />
                </div>

                {user ? (
                  <Button
                    type="button"
                    onClick={() => navigate("/marketplace")}
                    className="h-12 w-full text-slate-900 hover:brightness-95"
                    style={{ background: "#D4AF37" }}
                    data-testid="continue-portal-btn"
                  >
                    Continue as {user.email} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={loading}
                    className="h-12 w-full text-slate-900 hover:brightness-95"
                    style={{ background: "#D4AF37" }}
                    data-testid="enter-portal-btn"
                  >
                    {loading ? "Entering..." : "Enter Portal"} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}

                <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-4 w-4 brickstok-gold" />
                    <div className="text-xs leading-relaxed text-slate-600" data-testid="signin-compliance-line">
                      Structured with{" "}
                      <span className="font-semibold text-slate-800">independent statutory audit oversight</span>{" "}
                      and{" "}
                      <span className="font-semibold text-slate-800">specialist legal counsel</span>{" "}
                      aligned with UK commercial real estate frameworks.
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* About AJBN Connect */}
      <section id="about" className="border-t border-slate-200 bg-white" data-testid="about-section">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <div className="overline">About</div>
              <h2 className="mt-2 font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
                A community built for{" "}
                <span className="italic" style={{ color: "#D4AF37" }}>capital.</span>
              </h2>
              <div className="mt-6 inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">
                <Users className="h-3.5 w-3.5 brickstok-gold" /> Founded 2019
              </div>
            </div>
            <div className="lg:col-span-3">
              <p className="text-lg leading-relaxed text-slate-700">
                Founded in 2019,{" "}
                <a
                  href="https://connect.ajbn.co.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline underline-offset-4 decoration-2 transition-colors hover:opacity-80"
                  style={{ color: "#0F172A", textDecorationColor: "#D4AF37" }}
                  data-testid="ajbn-connect-link"
                >
                  AJBN CONNECT
                  <ArrowUpRight className="ml-1 inline h-4 w-4" style={{ color: "#D4AF37" }} />
                </a>{" "}
                is the UK’s only network dedicated to strengthening professional ties between Asian and Jewish business communities. Our mission is to foster a collaborative environment where leaders in Finance, Property, and Technology share best practices and drive collective growth.
              </p>
              <p className="mt-6 text-lg leading-relaxed text-slate-700">
                Brickstok serves as the institutional-grade bridge for this community.
              </p>

              <div
                className="mt-8 flex flex-col gap-4 rounded-lg border border-slate-200 bg-slate-50 p-6 sm:flex-row sm:items-center sm:justify-between"
                data-testid="leadership-card"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md font-display text-lg font-semibold text-slate-900"
                    style={{ background: "#D4AF37" }}
                    aria-hidden="true"
                  >
                    SP
                  </div>
                  <div>
                    <div className="overline">Leadership</div>
                    <div className="mt-1 font-display text-xl font-semibold text-slate-900" data-testid="leadership-name">
                      Salil Patankar
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      Capital Connect Lead · Founder & Mentor, AJBN iMPACT Lions Club
                    </div>
                  </div>
                </div>
                <a
                  href="https://connect.ajbn.co.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition-colors hover:border-[#D4AF37] hover:text-slate-900"
                  data-testid="leadership-ajbn-link"
                >
                  Visit AJBN Connect
                  <ArrowUpRight className="h-3.5 w-3.5" style={{ color: "#D4AF37" }} />
                </a>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-3" data-testid="about-pillars">
                {[
                  { label: "Finance", detail: "Structured capital markets access" },
                  { label: "Property", detail: "Prime UK commercial real estate" },
                  { label: "Technology", detail: "Institutional-grade infrastructure" },
                ].map((p) => (
                  <div key={p.label} className="rounded-md border border-slate-200 bg-white p-5">
                    <div className="overline">Sector</div>
                    <div className="mt-1 font-display text-xl font-semibold text-slate-900">{p.label}</div>
                    <div className="mt-2 text-sm text-slate-500">{p.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-slate-200 bg-slate-50" data-testid="faq-section">
        <div className="mx-auto max-w-5xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-3">
            <div>
              <div className="overline">Governance</div>
              <h2 className="mt-2 font-display text-4xl font-semibold tracking-tight text-slate-900">
                About & Governance
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-500">
                Structural questions we’re asked most often by institutions, family offices, and community members.
              </p>
              <a
                href="https://connect.ajbn.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-4"
                style={{ color: "#0F172A", textDecorationColor: "#D4AF37" }}
                data-testid="faq-ajbn-link"
              >
                Visit AJBN Connect <ExternalLink className="h-3.5 w-3.5" style={{ color: "#D4AF37" }} />
              </a>
            </div>
            <div className="lg:col-span-2">
              <Accordion type="single" collapsible className="w-full space-y-3" data-testid="faq-accordion">
                {FAQ.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <AccordionItem
                      key={item.q}
                      value={`item-${idx}`}
                      className="overflow-hidden rounded-md border border-slate-200 bg-white px-5 shadow-sm"
                      data-testid={`faq-item-${idx}`}
                    >
                      <AccordionTrigger className="py-5 text-left hover:no-underline" data-testid={`faq-trigger-${idx}`}>
                        <div className="flex items-center gap-3 pr-3">
                          <div
                            className="flex h-9 w-9 items-center justify-center rounded-md"
                            style={{ background: "rgba(212,175,55,0.12)" }}
                          >
                            <Icon className="h-4 w-4" style={{ color: "#8B6F1D" }} />
                          </div>
                          <div className="font-display text-lg font-semibold text-slate-900">{item.q}</div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 pl-[52px] pr-2 text-sm leading-relaxed text-slate-600" data-testid={`faq-content-${idx}`}>
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
